// Pure helpers for the flyover-video share flow. Kept out of the Vue component so the reuse decision
// and the social-share URL builders are unit-testable. Web reality: unlike the mobile apps, a browser
// can't push an MP4 into a specific app, so social targets share the recording LINK (rich OG preview),
// "Save video" downloads the MP4, and "Copy link" copies the video URL.

export interface FlyoverVideoLite {
  id?: string;
  status: string;
  url?: string | null;
  aspect?: string | null;
  renderedAt?: string | null;
}

/**
 * The flyover video to reuse, or null. Any READY video with a url is reusable — the backend deletes a
 * recording's videos when its route actually changes, so a surviving READY render always matches.
 * Newest render wins.
 */
export function pickReusableVideo(videos: FlyoverVideoLite[]): FlyoverVideoLite | null {
  const ready = videos.filter((v) => v.status === 'READY' && !!v.url);
  if (ready.length === 0) return null;
  return ready.reduce((best, v) => {
    const bestAt = Date.parse(best.renderedAt ?? '') || 0;
    const vAt = Date.parse(v.renderedAt ?? '') || 0;
    return vAt > bestAt ? v : best;
  });
}

/** Portrait (9:16) unless the aspect is explicitly landscape. */
export function isVerticalAspect(aspect?: string | null): boolean {
  return aspect !== 'LANDSCAPE_16_9' && aspect !== '16x9';
}

// GraphQL aspect enum values sent to the backend.
export const ASPECT_VERTICAL = 'PORTRAIT_9_16';
export const ASPECT_HORIZONTAL = 'LANDSCAPE_16_9';

export function facebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function emailShareUrl(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** A filename for the downloaded MP4, e.g. "my-hike-flyover.mp4". */
export function videoFileName(title: string): string {
  const slug = (title || 'hike')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);
  return `${slug || 'hike'}-flyover.mp4`;
}
